
import * as THREE from 'three';
import Experience from '../Experience.js';
import Resources from '../Utils/Resources.js';
import faceVertexShader from "../Shaders/face/face_vertex.glsl";
import faceFragmentShader from "../Shaders/face/face_fragment.glsl";
import Data from '../Utils/Data.js';

export default class Face {
    constructor(root) {
        this.root = root;

        this.experience = Experience.getInstance();
        this.scene = this.experience.scene;
        this.time = this.experience.time;
        this.renderer = this.experience.renderer.instance;
        this.debug = this.experience.debug;
        this.sizes = this.experience.sizes;
        this.resources = Resources.getInstance();
        this.data = Data.getInstance();

        this.textureLoader = new THREE.TextureLoader();

        this.init();
        this.loadtextures();
        this.createFace();

        this.blink();
    }

    init() {
        this.faceWidth = 1.2;
        this.faceHeight = 1.2;

        this.initialPosition = new THREE.Vector3(0, 0, this.data.sphereRadius + 0.01);

        this.expressionTarget = 0;
        this.expressionSpeed = 2;

        this.canBlink = true;

        this.previousState = null; //we take current state from Data
    }

    loadtextures() {
        this.angryTexture = this.resources.items.emotion_angry;
        this.aweTexture = this.resources.items.emotion_awe;
        this.blinkTexture = this.resources.items.emotion_blink;
        this.deadTexture = this.resources.items.emotion_dead;
        this.deadInsideTexture = this.resources.items.emotion_dead_inside;
        this.energyTexture = this.resources.items.emotion_energy;
        this.fullTexture = this.resources.items.emotion_full;
        this.happyTexture = this.resources.items.emotion_happy;
        this.loveTexture = this.resources.items.emotion_love;
        this.noEnergyTexture = this.resources.items.emotion_no_energy;
        this.normalTexture = this.resources.items.emotion_normal;
        this.sadTexture = this.resources.items.emotion_sad;
        // this.sadLittleTexture = this.resources.items.emotion_sad_little;
        this.tickleTexture = this.resources.items.emotion_tickle;
        this.whatTexture = this.resources.items.emotion_what;
        this.wtfTexture = this.resources.items.emotion_wtf;
    }

    createFace() {
        this.faceGeometry = new THREE.PlaneGeometry(this.faceWidth, this.faceHeight, 20, 20);

        this.faceMaterial = new THREE.ShaderMaterial({
            vertexShader: faceVertexShader,
            fragmentShader: faceFragmentShader,
            uniforms: {
                uRadius: { value: this.data.sphereRadius }, // Pass the sphere radius
                uTexture1: { value: this.normalTexture },
                uTexture2: { value: this.blinkTexture },
                uMixFactor: { value: 0.0 } // 0 means full Texture1, 1 means full Texture2
            },
            transparent: true,
            side: THREE.DoubleSide

        });

        this.face = new THREE.Mesh(this.faceGeometry, this.faceMaterial);
        this.face.position.set(this.initialPosition.x, this.initialPosition.y, this.initialPosition.z); // Slight offset to avoid z-fighting
        this.face.renderOrder = 2;
        this.root.add(this.face);
    }

    loadFaceExpresion() {
        if (this.previousState == this.data.currentState) return;
        switch (this.data.currentState) {
            case this.data.State.NORMAL:
                this.faceMaterial.uniforms.uTexture1.value = this.normalTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.AWE:
                this.faceMaterial.uniforms.uTexture1.value = this.aweTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.HAPPY:
                this.faceMaterial.uniforms.uTexture1.value = this.happyTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.WHAT:
                this.faceMaterial.uniforms.uTexture1.value = this.whatTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.LOVE:
                this.faceMaterial.uniforms.uTexture1.value = this.loveTexture;
                this.canBlink = false;
                break;
            case this.data.State.FULL:
                this.faceMaterial.uniforms.uTexture1.value = this.fullTexture;
                this.canBlink = false;
                break;
            case this.data.State.EXCITED:
                this.faceMaterial.uniforms.uTexture1.value = this.energyTexture;
                this.canBlink = false;
                break;
            case this.data.State.WTF:
                this.faceMaterial.uniforms.uTexture1.value = this.wtfTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.SAD:
                this.faceMaterial.uniforms.uTexture1.value = this.sadTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.ANGRY:
                this.faceMaterial.uniforms.uTexture1.value = this.angryTexture;
                if (!this.canBlink) {
                    this.canBlink = true;
                    this.blink();
                }
                break;
            case this.data.State.HATE:
                this.faceMaterial.uniforms.uTexture1.value = this.deadInsideTexture;
                this.canBlink = false;
                break;
            case this.data.State.HUNGRY:
                this.faceMaterial.uniforms.uTexture1.value = this.deadTexture;
                this.canBlink = false;
                break;
            case this.data.State.DEPRESSED:
                this.faceMaterial.uniforms.uTexture1.value = this.noEnergyTexture;
                this.canBlink = false;
                break;
        }
        this.previousState = this.data.currentState;
    }

    async blink() {
        while(this.canBlink)
        {
            this.expressionTarget = 0;
            await this.wait(Math.random() * 3500 + 2500) //wait between 2.5 and 7 seg before blinking again
            
            if (!this.canBlink) break; // Stop execution if the flag is false

            this.expressionTarget = 1; //blink
            await this.wait(200); //fast
        }
    }

    wait(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    update() {
        // INTERPOLATION BETWEEN TEXTURES OF THE FACE
        let currentMix = this.faceMaterial.uniforms.uMixFactor.value;
        let deltaTime = this.time.delta * 0.005; // Convert ms to seconds
        let mixSpeed = this.expressionSpeed * deltaTime; // Control speed
        this.faceMaterial.uniforms.uMixFactor.value += (this.expressionTarget - currentMix) * mixSpeed;
    }

    resize() {

    }

}